import moment from 'moment'
import MediaScheduleMixin from '@/components/mixins/media-schedule-mixin'
import DateTimePicker from '@/components/common/datetimepicker/datetimepicker.vue'
import { mapGetters } from 'vuex'

export default {
  name: 'playtimeForm',
  inject: [
    '$currentUser',
    '$utils'
  ],
  components: {
    DateTimePicker
  },
  props: {
    playtimeProp: {
      type: Object,
      default () { return {} }
    },
    mediaSelectionProp: {
      type: Array,
      default () { return [] }
    },
    parentAccessorComponentProp: {
      default () { return 'mediaPage' }
    }
  },
  watch: {
    playtimeProp: {
      handler (newVal, previousVal) {
        const uidChanged = newVal.uid !== previousVal.uid
        const playerSelectionChanged = (newVal._edit && previousVal._edit) && (newVal._edit.playerSelection !== previousVal._edit.playerSelection)

        if (uidChanged || playerSelectionChanged) {
          this.handleScheduleOptionsVisibility()
          this.handlePlaytimeDataFromParentChanged(newVal)
        }
      },
      deep: true
    },
    mediaSelectionProp: {
      handler (newVal, previousVal) {
        this.mediaSelection = newVal
        this.handleScheduleOptionsVisibility()
      },
      deep: true
    }
  },
  computed: {
    allScheduleTypes () {
      return this.modifySchedule.options.type.every((e) => { return e.visible })
    },
    hideAllScheduleTypes () {
      return this.modifySchedule.options.type.every((e) => { return !e.visible })
    },
    ...mapGetters({
      scheduleTimeFormat: 'preferredTimeFormat'
    })
  },
  mixins: [MediaScheduleMixin],
  data () {
    return {
      parentAccessorComponent: this.parentAccessorComponentProp,
      componentUsedInSchedulePage: this.parentAccessorComponentProp === 'schedulePage',
      componentUsedInProjectTemplateMenu: this.parentAccessorComponentProp === 'projectTemplateMenu',
      componentUsedInPlayersPageMenu: this.parentAccessorComponentProp === 'playersPageMenu',
      componentUsedInSmartTextPageMenu: this.parentAccessorComponentProp === 'smartTextPageMenu',
      playtimeData: this.playtimeProp,
      mediaSelection: this.mediaSelectionProp,
      datePickerOptions: { firstDayOfWeek: 1, format: 'mm/YY' },
      scheduleTypeOptions: { daily: 'daily', custom: 'custom' },
      switchTypeValOptions: { daily: 'daily', custom: 'custom', priority: 'priority' },
      scheduleTypeSelected: null,
      selectedPlaytimeItem: null,
      modifyScheduleCopy: null,
      modifySchedule: {
        formValues: {
          startDate: '',
          endDate: '',
          type: null,
          switchTypeVal: null,
          startTime: '',
          endTime: '',
          repeatDays: [],
          showDateRange: true,
          showTimeRange: true
        },
        options: {
          type: [
            { value: 'daily', switchTypeVal: 'daily', label: '24/7', elClass: 'schedule-type-daily', visible: true, toolTip: '24/7' },
            { value: 'custom', switchTypeVal: 'custom', label: this.$t('label.custom'), elClass: 'schedule-type-custom', visible: true, toolTip: this.$t('label.schedule') },
            { value: 'custom', switchTypeVal: 'priority', label: this.$t('label.priority'), elClass: 'schedule-type-priority', visible: true, toolTip: this.$t('label.priority') }
          ],
          days: { mon: this.$t('label.mon'), tue: this.$t('label.tue'), wed: this.$t('label.wed'), thu: this.$t('label.thu'), fri: this.$t('label.fri'), sat: this.$t('label.sat'), sun: this.$t('label.sun') }
        }
      },
      scheduleTimeListActions: { createNewPlayTime: 'createNewPlayTime', updatePlayTime: 'updatePlayTime', deletePlayTime: 'deletePlayTime' },
      scheduleTimeList: [],
      mouseOverPlaytimeItem: null,
      prevScheduleSwitchTypeClick: null
    }
  },
  methods: {
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('playtime-form-change', { action, payload: { data } })
      }
    },
    handleScheduleOptionsVisibility () {
      const modifySchedule = this.$utils.clone(this.modifySchedule)
      const hasOverlayInMediaSelection = parseInt(this.mediaSelection.findIndex(media => media.skin || ['audio', 'audio-playlist'].indexOf(media.type) >= 0)) >= 0

      modifySchedule.options.type.forEach((option, indx) => {
        modifySchedule.options.type[indx].visible = true

        if (option.switchTypeVal === this.switchTypeValOptions.priority) {
          // Hide priority option for not extended user or media overlay
          if (!this.$currentUser.hasGroupExtended() || hasOverlayInMediaSelection) {
            modifySchedule.options.type[indx].visible = false
          }
        }

        // Hide all priority for project template menu or players menu
        if ((this.componentUsedInProjectTemplateMenu || this.componentUsedInPlayersPageMenu || this.componentUsedInSmartTextPageMenu)) {
          modifySchedule.options.type[indx].visible = false
        }
      })

      this.modifySchedule = modifySchedule
    },
    handlePlaytimeDataFromParentChanged (playTime) {
      // Set modifySchedule data based from selected playtime
      const modifySchedule = this.$utils.clone(this.modifySchedule)
      const startTimeVal = this.createStartTimeFromAPIFormat(playTime)
      const endTimeVal = this.createEndTimeFromAPIFormat(playTime)
      const isDailyCurrentPlayTime = playTime.daily
      const isPriority = !isDailyCurrentPlayTime && playTime.priority

      if (isDailyCurrentPlayTime) {
        modifySchedule.formValues.type = this.scheduleTypeOptions.daily
        modifySchedule.formValues.switchTypeVal = this.scheduleTypeOptions.daily
        modifySchedule.formValues.startDate = ''
        modifySchedule.formValues.endDate = ''
        modifySchedule.formValues.startTime = ''
        modifySchedule.formValues.endTime = ''
        modifySchedule.formValues.repeatDays = []
        modifySchedule.formValues.priority = isPriority
      } else {
        modifySchedule.formValues = {
          type: (playTime.daily) ? this.scheduleTypeOptions.daily : this.scheduleTypeOptions.custom,
          switchTypeVal: (isPriority) ? this.switchTypeValOptions.priority : this.switchTypeValOptions.custom,
          startDate: playTime.startDate,
          endDate: playTime.endDate,
          startTime: startTimeVal,
          endTime: endTimeVal,
          repeatDays: (playTime.repeatDays) ? playTime.repeatDays : [],
          showDateRange: true,
          showTimeRange: true,
          priority: isPriority
        }
      }

      // No default data if multiple playtime is not the same
      if (playTime.multiplePlaytimeSummary && !playTime.allPlaytimeTheSame) {
        modifySchedule.formValues.type = null
        modifySchedule.formValues.switchTypeVal = null
        modifySchedule.formValues.startDate = ''
        modifySchedule.formValues.endDate = ''
        modifySchedule.startTime = ''
        modifySchedule.endTime = ''
        modifySchedule.repeatDays = []
        modifySchedule.priority = false
      }

      this.modifySchedule = modifySchedule
      this.modifyScheduleCopy = this.$utils.clone(this.modifySchedule)
    },
    getModifyScheduleFormValueBackendFormat () {
      const modifySchedule = this.$utils.clone(this.modifySchedule)
      const selectedScheduleType = modifySchedule.formValues.type
      let formData = { daily: true, priority: false }

      if (selectedScheduleType === this.scheduleTypeOptions.custom) {
        formData = {
          startDate: modifySchedule.formValues.startDate,
          endDate: modifySchedule.formValues.endDate,
          startTime: this.getStartTimeFromTimeRange(modifySchedule.formValues.startTime, modifySchedule.formValues.endTime),
          duration: this.calculateDurationFromTimeRange(modifySchedule.formValues.startTime, modifySchedule.formValues.endTime),
          repeatDays: modifySchedule.formValues.repeatDays,
          priority: false
        }

        // Sort repeat days by day
        if (Array.isArray(formData.repeatDays) && formData.repeatDays.length) {
          const newRepeateDaysVal = []
          const repeatDaysOrder = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
          repeatDaysOrder.forEach((dayOrder) => {
            if (formData.repeatDays.indexOf(dayOrder) >= 0) {
              newRepeateDaysVal.push(dayOrder)
            }
          })
          formData.repeatDays = newRepeateDaysVal
        }

        // Set priority
        if (modifySchedule.formValues.switchTypeVal === this.switchTypeValOptions.priority) {
          formData.priority = true
        }
      }

      // Set priority
      if (modifySchedule.formValues.switchTypeVal === this.switchTypeValOptions.priority) {
        formData.priority = true
      }

      return formData
    },
    handleScheduleTypeClick (scheduleType, switchTypeVal) {
      if (this.modifySchedule.formValues.switchTypeVal === switchTypeVal) {
        return
      }

      this.prevScheduleSwitchTypeClick = switchTypeVal

      const modifySchedule = this.$utils.clone(this.modifySchedule)
      modifySchedule.formValues.type = scheduleType
      modifySchedule.formValues.switchTypeVal = switchTypeVal
      this.modifySchedule = modifySchedule
      this.handleModifySchedulePropertyChange()
    },
    isModifyFormIsNotEmpty () {
      const modifySchedule = this.$utils.clone(this.modifySchedule)
      const formValues = modifySchedule.formValues
      const hasRepeatDays = !!((Array.isArray(formValues.repeatDays)) && formValues.repeatDays.length)
      const hasStartDateAndEndDate = !!formValues.startDate && !!formValues.endDate
      const hasStartAndEndTime = !!formValues.startTime && !!formValues.endTime

      if (hasRepeatDays || hasStartDateAndEndDate || hasStartAndEndTime) {
        return true
      }
      return false
    },
    handleUpdateFromDateTimePicker (property, value) {
      this.modifySchedule.formValues[property] = value
      this.handleModifySchedulePropertyChange()
    },
    handleModifySchedulePropertyChange () {
      this.handleSubmitDataToParent()
    },
    handleCustomScheduleIconClick (iconName) {
      const modifySchedule = this.$utils.clone(this.modifySchedule)

      if (iconName === 'dateRangeIcon') {
        modifySchedule.formValues.showDateRange = true
      }

      if (iconName === 'timeRangeIcon') {
        modifySchedule.formValues.showTimeRange = true
      }
      this.modifySchedule = modifySchedule
    },
    handleDatePickerFocus () {
      // Adjust DatePicker component Panel Header date format to MMM YYYY
      this.$refs.scheduleDatePickerRef.panel.computed.leftLabel = function () {
        const lefMonthTransId = moment().month(this.leftDate.getMonth()).format('MMM').toLowerCase()

        return this.t(`el.datepicker.months.${lefMonthTransId}`) + ' ' + this.leftDate.getFullYear() + ' ' + this.t('el.datepicker.year')
      }

      this.$refs.scheduleDatePickerRef.panel.computed.rightLabel = function () {
        const rightMonthTransId = moment().month(this.rightDate.getMonth()).format('MMM').toLowerCase()

        return this.t(`el.datepicker.months.${rightMonthTransId}`) + ' ' + this.rightDate.getFullYear() + ' ' + this.t('el.datepicker.year')
      }
    },
    /**
     * Handle Modify Schedule Time Picker element to limit time slot choice to 10min step
     * @param {String} timePickerDropdownClass
     */
    handleModifyScheduleTimePickerFocus (timePickerDropdownClass) {
      const modifySchedule = this.$utils.clone(this.modifySchedule)

      /* Set Default Value
      If empty then set a default value.Use xx:00 as default value if current minute is not available from the options
      */
      const getDefaultValue = () => {
        if ((moment().format('mm') % 10) !== 0) {
          return moment().set({ minute: 0 })
        }

        return moment()
      }

      if (timePickerDropdownClass === 'schedule-time-picker-dropdown-start' &&
        (modifySchedule.formValues.startTime === '' || modifySchedule.formValues.startTime === null)) {
        modifySchedule.formValues.startTime = getDefaultValue()
      }

      if (timePickerDropdownClass === 'schedule-time-picker-dropdown-end' &&
        (modifySchedule.formValues.endTime === '' || modifySchedule.formValues.endTime === null)) {
        modifySchedule.formValues.endTime = getDefaultValue()
      }

      this.modifySchedule = modifySchedule
    },
    handleNewScheduleClick () {
      this.showModifyScheduleForm = true
      this.clearModifyScheduleData({ clearScheduleType: true })
    },
    handleSubmitDataToParent () {
      this.showModifyScheduleForm = false
      let playtimeData = this.getModifyScheduleFormValueBackendFormat()

      if (this.modifySchedule.formValues.type === this.scheduleTypeOptions.daily) {
        playtimeData = { daily: true }
      }

      if ((this.modifySchedule.formValues.type === this.scheduleTypeOptions.custom) && (playtimeData.daily)) {
        delete playtimeData.daily
      }

      // Playtime that has no days, no date and no time should be converted to daily
      if (!playtimeData.daily) {
        const hasRepeatDays = !!((Array.isArray(playtimeData.repeatDays)) && playtimeData.repeatDays.length)
        const hasStartDateAndEndDate = !!playtimeData.startDate && !!playtimeData.endDate
        const hasStartAndEndTime = !!playtimeData.startTime
        const isNewPlaytimeHasData = hasRepeatDays || hasStartDateAndEndDate || hasStartAndEndTime

        if (!isNewPlaytimeHasData && !this.prevScheduleSwitchTypeClick) {
          playtimeData = { daily: true }
        }
      }

      const payload = { playtimeData }
      this.prevScheduleSwitchTypeClick = null
      this.emitEvent('PLAYTIME_FORM_UPDATED', payload)
    },
    clearModifyScheduleData (params) {
      const modifySchedule = this.$utils.clone(this.modifySchedule)
      modifySchedule.formValues.startDate = ''
      modifySchedule.formValues.endDate = ''
      modifySchedule.formValues.startTime = ''
      modifySchedule.formValues.endTime = ''
      modifySchedule.formValues.repeatDays = []
      modifySchedule.formValues.showDateRange = true
      modifySchedule.formValues.showTimeRange = true

      if (typeof params === 'object' && params.clearScheduleType) {
        modifySchedule.formValues.type = this.scheduleTypeOptions.daily
        modifySchedule.formValues.switchTypeVal = this.switchTypeValOptions.daily
      }

      this.modifySchedule = modifySchedule
    },
    isDisabledEndDate (date) {
      const startDate = new Date(this.modifySchedule.formValues.startDate)
      startDate.setHours(0, 0, 0, 0)
      return date < startDate
    },
    isDisabledStartDate (date) {
      let endDate = this.modifySchedule.formValues.endDate

      if (endDate) {
        endDate = new Date(endDate)
        endDate.setHours(0, 0, 0, 0)
        return date > endDate
      }

      return false
    },
    disabledMinutes (hour) {
      return [1, 2, 3, 4, 5]
    },
    updateDaySelection () {
      const weekStart = this.$utils.getCalendarWeekStart(navigator.language);
      const daysOrder = weekStart === 1
        ? ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']
        : ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

      const dayLabels = {
        mon: this.$t('label.mon'),
        tue: this.$t('label.tue'),
        wed: this.$t('label.wed'),
        thu: this.$t('label.thu'),
        fri: this.$t('label.fri'),
        sat: this.$t('label.sat'),
        sun: this.$t('label.sun'),
      };

      this.modifySchedule.options.days = {};
      daysOrder.forEach(day => {
        this.modifySchedule.options.days[day] = dayLabels[day];
      });
    }
  },
  beforeUnmount () {
    this.prevScheduleSwitchTypeClick = null
  },
  mounted () {
    this.updateDaySelection();
    this.handleScheduleOptionsVisibility()
    this.handlePlaytimeDataFromParentChanged(this.playtimeProp)
  }
}
